import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

type ScriptVersion = {
  id: string;
  version: number;
  title: string;
  content: string;
  description: string;
  createdAt: string;
};

type DiffLine = {
  type: 'added' | 'removed' | 'unchanged';
  content: string;
  lineNumber?: number;
};

function normalizeText(text: string): string {
  return text
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\r/g, '\n')   // Handle any remaining \r
    .replace(/\s+$/gm, ''); // Trim trailing whitespace
}

function areLinesSimilar(line1: string, line2: string): boolean {
  const normalized1 = line1.trim();
  const normalized2 = line2.trim();
  return normalized1 === normalized2;
}

function computeTextDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = normalizeText(oldText).split('\n');
  const newLines = normalizeText(newText).split('\n');
  const diff: DiffLine[] = [];
  
  let i = 0, j = 0;
  
  while (i < oldLines.length || j < newLines.length) {
    // If we're at the end of one array, add remaining lines
    if (i >= oldLines.length) {
      const line = newLines[j];
      if (line !== undefined) {
        diff.push({ type: 'added', content: line, lineNumber: j + 1 });
      }
      j++;
      continue;
    }
    if (j >= newLines.length) {
      const line = oldLines[i];
      if (line !== undefined) {
        diff.push({ type: 'removed', content: line, lineNumber: i + 1 });
      }
      i++;
      continue;
    }

    const oldLine = oldLines[i];
    const newLine = newLines[j];

    if (oldLine === undefined || newLine === undefined) {
      i++;
      j++;
      continue;
    }

    // Check if lines are similar
    if (areLinesSimilar(oldLine, newLine)) {
      // Lines are similar, mark as unchanged
      diff.push({ type: 'unchanged', content: oldLine, lineNumber: i + 1 });
      i++;
      j++;
    } else {
      // Look ahead to find the next matching line
      let foundMatch = false;
      const lookAhead = 3; // How many lines to look ahead
      
      // Look ahead in new text
      for (let k = 1; k <= lookAhead && j + k < newLines.length; k++) {
        const lookAheadLine = newLines[j + k];
        if (lookAheadLine !== undefined && areLinesSimilar(oldLine, lookAheadLine)) {
          // Found a match ahead in new text, mark intermediate lines as added
          for (let l = 0; l < k; l++) {
            const addedLine = newLines[j + l];
            if (addedLine !== undefined) {
              diff.push({ type: 'added', content: addedLine, lineNumber: j + l + 1 });
            }
          }
          j += k;
          foundMatch = true;
          break;
        }
      }
      
      // Look ahead in old text
      if (!foundMatch) {
        for (let k = 1; k <= lookAhead && i + k < oldLines.length; k++) {
          const lookAheadLine = oldLines[i + k];
          if (lookAheadLine !== undefined && areLinesSimilar(lookAheadLine, newLine)) {
            // Found a match ahead in old text, mark intermediate lines as removed
            for (let l = 0; l < k; l++) {
              const removedLine = oldLines[i + l];
              if (removedLine !== undefined) {
                diff.push({ type: 'removed', content: removedLine, lineNumber: i + l + 1 });
              }
            }
            i += k;
            foundMatch = true;
            break;
          }
        }
      }
      
      // If no match found, mark current line as changed
      if (!foundMatch) {
        diff.push({ type: 'removed', content: oldLine, lineNumber: i + 1 });
        diff.push({ type: 'added', content: newLine, lineNumber: j + 1 });
        i++;
        j++;
      }
    }
  }
  
  return diff;
}

function DiffBox({ title, diff, showOnlyChanges }: { title: string; diff: DiffLine[]; showOnlyChanges: boolean }) {
  // Filter to only show changes if showOnlyChanges is true
  const filteredDiff = showOnlyChanges
    ? diff.filter((line) => line.type !== 'unchanged')
    : diff;

  if (filteredDiff.length === 0) {
    return null;
  }

  // Group consecutive lines of the same type
  const groups: { type: DiffLine['type']; lines: DiffLine[] }[] = [];
  let currentGroup: { type: DiffLine['type']; lines: DiffLine[] } | null = null;

  filteredDiff.forEach((line) => {
    if (!currentGroup || currentGroup.type !== line.type) {
      currentGroup = { type: line.type, lines: [] };
      groups.push(currentGroup);
    }
    currentGroup.lines.push(line);
  });

  return (
    <div className="mt-2 border rounded-lg p-4">
      <h4 className="text-lg font-medium mb-4">{title}</h4>
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
        <div className="space-y-2">
          {groups.map((group, groupIndex) => (
            <div key={groupIndex} className="relative">
              {showOnlyChanges && group.lines.length > 0 && (
                <div className="absolute -left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
              )}
              {group.lines.map((line, lineIndex) => (
                <div
                  key={lineIndex}
                  className={`pl-8 ${
                    line.type === 'added'
                      ? 'bg-green-50 text-green-700'
                      : line.type === 'removed'
                      ? 'bg-red-50 text-red-700'
                      : 'text-gray-700'
                  }`}
                >
                  <span className="inline-block w-12 text-gray-500 text-right mr-4">
                    {line.lineNumber}
                  </span>
                  <span className="inline-block w-4 text-gray-500 mr-2">
                    {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
                  </span>
                  {line.content}
                </div>
              ))}
              {showOnlyChanges && group.lines.length > 0 && (
                <div className="h-4 border-l border-gray-200 ml-[-1rem]" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ScriptVersionHistory({ versions, currentScript }: { versions: ScriptVersion[], currentScript: { title: string, description: string, content: string } }) {
  const [selectedVersion, setSelectedVersion] = useState<ScriptVersion | null>(null);
  const [showOnlyChanges, setShowOnlyChanges] = useState(true);

  const scrollToVersionHistory = () => {
    const element = document.getElementById('body-root');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div id="version-history" className="mt-8 pt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-medium">Version History</h3>
          <button
            onClick={scrollToVersionHistory}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Scroll to Top
          </button>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showOnlyChanges}
              onChange={(e) => setShowOnlyChanges(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show only changes
          </label>
        </div>
      </div>

      <div className="space-y-4">

        {versions.map((version) => {
          const prevVersion = versions.find((v) => v.version === version.version - 1);
          
          const hasChanges = prevVersion && (
            prevVersion.title !== version.title ||
            prevVersion.description !== version.description ||
            prevVersion.content !== version.content
          );

          return (
            <div key={version.id}>
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    {
                      version.version === 0 ? (
                        <span className="text-sm">
                          Original Version Saved - {new Date(version.createdAt).toLocaleString()} - {formatDistanceToNow(new Date(version.createdAt), { addSuffix: true })}
                        </span>
                      ) : (
                        <span className="text-sm">
                          Version {version.version} - {new Date(version.createdAt).toLocaleString()} - {formatDistanceToNow(new Date(version.createdAt), { addSuffix: true })}
                        </span>
                      )
                    }
                   
                    {version.title && (
                      <h4 className="text-sm font-medium">{version.title}</h4>
                    )}
                  </div>
                  {hasChanges ? (
                    <button
                      onClick={() => {
                        setSelectedVersion(selectedVersion?.id === version.id ? null : version);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {selectedVersion?.id === version.id ? 'Hide Changes' : 'View Changes'}
                    </button>
                  ) : (
                    <span className="text-sm">
                      No changes to display
                    </span>
                  )}
                </div>
              </div>

              {selectedVersion?.id === version.id && prevVersion && (
                <>
                  {prevVersion.title !== version.title && (
                    <DiffBox
                      title="Title Changes"
                      diff={computeTextDiff(prevVersion.title || '', version.title || '')}
                      showOnlyChanges={showOnlyChanges}
                    />
                  )}
                  {prevVersion.description !== version.description && (
                    <DiffBox
                      title="Description Changes"
                      diff={computeTextDiff(prevVersion.description || '', version.description || '')}
                      showOnlyChanges={showOnlyChanges}
                    />
                  )}
                  {prevVersion.content !== version.content && (
                    <DiffBox
                      title="Content Changes"
                      diff={computeTextDiff(prevVersion.content || '', version.content || '')}
                      showOnlyChanges={showOnlyChanges}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
} 